import { inject, ProviderToken } from '@angular/core';
import { last } from 'lodash';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IrisValueStorage } from './value-storage';

export type IrisValueAutoSaverFactory<T> = () => IrisValueAutoSaver<T>;

export type IrisValueAutoSaverBuilderFactory<T> = IrisValueAutoSaverFactory<T> & {
  setStorage(storageToken: ProviderToken<IrisValueStorage<T>>): IrisValueAutoSaverBuilderFactory<T>;
};

export function valueAutoSaverFactory<T>(
  valueSourceToken: ProviderToken<IrisValueAutoSaverSource<T>>,
): IrisValueAutoSaverBuilderFactory<T> {
  const storagesTokens: ProviderToken<IrisValueStorage<T>>[] = [];

  const factory = (() => {
    const storages = storagesTokens.map(storage => inject(storage));
    return new IrisValueAutoSaver(storages, inject(valueSourceToken));
  }) as IrisValueAutoSaverBuilderFactory<T>;

  factory.setStorage = (storageToken: ProviderToken<IrisValueStorage<T>>) => {
    storagesTokens.push(storageToken);
    return factory;
  };

  return factory;
}

export interface IrisValueAutoSaverSource<T> {
  value$: Observable<T>;
  setValue(value: T): void;
}

export type IrisValueAutoSaverSourceFactory<T> = () => IrisValueAutoSaverSource<T>;

export class IrisValueAutoSaver<T> {
  constructor(
    private readonly storages: IrisValueStorage<T>[],
    private readonly valueSource: IrisValueAutoSaverSource<T>,
  ) {
    const value = this.getValue();

    if (value) {
      valueSource.setValue(value);
    }

    valueSource.value$
      .pipe(
        tap(value => this.saveValue(value)),
      )
      .subscribe();
  }

  rereadValues(): void {
    this.valueSource.setValue(this.getValue() ?? null);
  }

  private getValue(): T | null {
    return last(this.storages
      .map(storage => storage.get())
      .filter(values => !!values),
    ) ?? null;
  }

  private saveValue(value: T): void {
    this.storages.forEach(storage => storage.set(value));
  }
}
